import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { KNOWN_PERSON_COLUMN_IDS } from "../constants/columnIds";

// Create the context
const MondayContext = createContext(null);

/**
 * MondayProvider - Provides global Monday.com data to the entire app
 * 
 * Stores:
 * - Properties (propertyKeyMap, availableProperties)
 * - Users/People (personColumnId, personColumnIdMap, availablePeople)
 * - Tasks (tasksByProperty, propertyNameToIdMap, loading, error)
 * - Board-level metadata
 */
export function MondayProvider({ children }) {
  // Properties state
  const [propertyKeyMap, setPropertyKeyMap] = useState({}); // Map of property ID -> Key # value
  const [availableProperties, setAvailableProperties] = useState([]); // List of properties for dropdown
  const [propertyDetailsMap, setPropertyDetailsMap] = useState({}); // Map of property ID -> { keybox, alarm, doorGateCode }

  // Person/User state
  const [personColumnId, setPersonColumnId] = useState(null); // Person column ID (backward compatibility)
  // Initialize person column map with known IDs immediately
  const [personColumnIdMap, setPersonColumnIdMap] = useState(() => {
    const initialMap = {};
    Object.keys(KNOWN_PERSON_COLUMN_IDS).forEach(boardIdStr => {
      const boardId = Number(boardIdStr);
      initialMap[boardId] = KNOWN_PERSON_COLUMN_IDS[boardId];
    });
    return initialMap;
  }); // Map of board ID -> person column ID
  const [availablePeople, setAvailablePeople] = useState([]); // List of available people/users

  // Tasks state
  const [rawItems, setRawItems] = useState([]); // Raw items from API (cached, fetched once per session)
  const [tasksByProperty, setTasksByProperty] = useState({}); // Tasks grouped by property (or by day in weekly view)
  const [propertyNameToIdMap, setPropertyNameToIdMap] = useState({}); // Map of property name -> property ID
  const [tasksLoading, setTasksLoading] = useState(false); // Loading state for tasks
  const [tasksError, setTasksError] = useState(null); // Error state for tasks
  const [rawItemsFetched, setRawItemsFetched] = useState(false); // Flag to track if raw items have been fetched

  // Board-level metadata (can be extended with columns, board configs, etc.)
  const [boardMetadata, setBoardMetadata] = useState({}); // Store board-level metadata

  // Helper function to update property key map
  const updatePropertyKeyMap = useCallback((keyMap) => {
    setPropertyKeyMap(keyMap);
  }, []);

  // Helper function to update available properties
  const updateAvailableProperties = useCallback((properties) => {
    setAvailableProperties(properties);
  }, []);

  // Helper function to update property details map
  const updatePropertyDetailsMap = useCallback((detailsMap) => {
    setPropertyDetailsMap(detailsMap);
  }, []);

  // Helper function to update person column ID
  const updatePersonColumnId = useCallback((columnId) => {
    setPersonColumnId(columnId);
  }, []);

  // Helper function to update person column ID map
  const updatePersonColumnIdMap = useCallback((columnMap) => {
    setPersonColumnIdMap(columnMap);
  }, []);

  // Helper function to update available people
  const updateAvailablePeople = useCallback((people) => {
    setAvailablePeople(people);
  }, []);

  // Helper function to update tasks by property
  const updateTasksByProperty = useCallback((tasks) => {
    setTasksByProperty(tasks);
  }, []);

  // Helper function to update property name to ID map
  const updatePropertyNameToIdMap = useCallback((nameMap) => {
    setPropertyNameToIdMap(nameMap);
  }, []);

  // Helper function to set tasks loading state
  const setTasksLoadingState = useCallback((loading) => {
    setTasksLoading(loading);
  }, []);

  // Helper function to set tasks error state
  const setTasksErrorState = useCallback((error) => {
    setTasksError(error);
  }, []);

  // Helper function to update board metadata
  const updateBoardMetadata = useCallback((metadata) => {
    setBoardMetadata(metadata);
  }, []);

  // Helper function to update raw items
  const updateRawItems = useCallback((items) => {
    setRawItems(items);
    setRawItemsFetched(true);
  }, []);

  // Helper function to reset raw items cache (for refresh)
  const resetRawItemsCache = useCallback(() => {
    setRawItems([]);
    setRawItemsFetched(false);
  }, []);

  // Memoize context value to prevent unnecessary re-renders of all consumers
  // Only recreate when actual values change, not on every render
  const value = useMemo(() => ({
    // Properties
    propertyKeyMap,
    availableProperties,
    propertyDetailsMap,
    updatePropertyKeyMap,
    updateAvailableProperties,
    updatePropertyDetailsMap,

    // People/Users
    personColumnId,
    personColumnIdMap,
    availablePeople,
    updatePersonColumnId,
    updatePersonColumnIdMap,
    updateAvailablePeople,

    // Tasks
    rawItems,
    rawItemsFetched,
    tasksByProperty,
    propertyNameToIdMap,
    tasksLoading,
    tasksError,
    updateRawItems,
    resetRawItemsCache,
    updateTasksByProperty,
    updatePropertyNameToIdMap,
    setTasksLoadingState,
    setTasksErrorState,

    // Board metadata
    boardMetadata,
    updateBoardMetadata,
  }), [
    // Include all state values - React will do shallow comparison
    // When state updates via setState, new references are created, triggering recalculation
    propertyKeyMap,
    availableProperties,
    propertyDetailsMap,
    personColumnId,
    personColumnIdMap,
    availablePeople,
    rawItems,
    rawItemsFetched,
    tasksByProperty,
    propertyNameToIdMap,
    tasksLoading,
    tasksError,
    boardMetadata,
    // Update functions are stable (useCallback with empty deps), but included for completeness
    updatePropertyKeyMap,
    updateAvailableProperties,
    updatePropertyDetailsMap,
    updatePersonColumnId,
    updatePersonColumnIdMap,
    updateAvailablePeople,
    updateRawItems,
    resetRawItemsCache,
    updateTasksByProperty,
    updatePropertyNameToIdMap,
    setTasksLoadingState,
    setTasksErrorState,
    updateBoardMetadata,
  ]);

  return (
    <MondayContext.Provider value={value}>
      {children}
    </MondayContext.Provider>
  );
}

/**
 * useMondayContext - Hook to access Monday.com global context
 * 
 * @returns {Object} Monday context value with all properties, people, tasks, and metadata
 * 
 * @example
 * const { availableProperties, tasksByProperty, updateTasksByProperty } = useMondayContext();
 */
export function useMondayContext() {
  const context = useContext(MondayContext);
  
  if (!context) {
    throw new Error("useMondayContext must be used within a MondayProvider");
  }
  
  return context;
}

